/**
 * Component Picker — flood-fill based object isolation for merged meshes.
 *
 * When a 3D model is loaded as a single merged mesh (all pipes in one geometry),
 * this module can isolate individual objects by finding connected face components.
 *
 * How it works:
 * 1. Build a vertex→face adjacency map (cached per geometry UUID)
 * 2. On pick: get the hit face index from raycaster
 * 3. BFS/flood-fill from the hit face to all connected faces (sharing vertices)
 * 4. Return the set of face indices belonging to that object
 *
 * Performance:
 * - Adjacency build: ~100ms for 1M faces (one-time, cached)
 * - Flood-fill per pick: <5ms for typical engineering objects (20-500 faces)
 */

import type { BufferGeometry } from 'three';

interface AdjacencyIndex {
  vertexToFaces: Map<number, number[]>;
  faceCount: number;
  geometryUuid: string;
}

// Cache adjacency indices per geometry UUID to avoid recomputation
const adjacencyCache = new Map<string, AdjacencyIndex>();

type UnknownRecord = Record<string, unknown>;

/**
 * Build or retrieve cached vertex→face adjacency for a geometry.
 */
function getAdjacency(geometry: BufferGeometry): AdjacencyIndex | null {
  const uuid = geometry.uuid;
  if (adjacencyCache.has(uuid)) {
    return adjacencyCache.get(uuid)!;
  }

  const index = geometry.index;
  if (!index) return null;

  const indexArray = index.array;
  const faceCount = Math.floor(indexArray.length / 3);
  const vertexToFaces = new Map<number, number[]>();

  for (let fi = 0; fi < faceCount; fi++) {
    const i0 = indexArray[fi * 3];
    const i1 = indexArray[fi * 3 + 1];
    const i2 = indexArray[fi * 3 + 2];

    if (!vertexToFaces.has(i0)) vertexToFaces.set(i0, []);
    if (!vertexToFaces.has(i1)) vertexToFaces.set(i1, []);
    if (!vertexToFaces.has(i2)) vertexToFaces.set(i2, []);

    vertexToFaces.get(i0)!.push(fi);
    vertexToFaces.get(i1)!.push(fi);
    vertexToFaces.get(i2)!.push(fi);
  }

  const adj: AdjacencyIndex = { vertexToFaces, faceCount, geometryUuid: uuid };
  adjacencyCache.set(uuid, adj);
  return adj;
}

/**
 * Build adjacency for non-indexed geometry (each 3 consecutive verts = 1 face).
 */
function getAdjacencyNonIndexed(geometry: BufferGeometry): AdjacencyIndex | null {
  const uuid = geometry.uuid;
  if (adjacencyCache.has(uuid)) {
    return adjacencyCache.get(uuid)!;
  }

  const position = geometry.attributes.position;
  if (!position) return null;

  const vertCount = position.count;
  const faceCount = Math.floor(vertCount / 3);

  // For non-indexed geometry, vertices are unique per face.
  // We need to identify shared vertices by position (epsilon comparison).
  // This is more expensive but necessary for Draco-decoded meshes.
  const posArray = position.array as Float32Array;
  const EPSILON = 1e-6;

  // Group vertices by quantized position
  const posMap = new Map<string, number[]>();
  for (let i = 0; i < vertCount; i++) {
    const x = Math.round(posArray[i * 3] / EPSILON) * EPSILON;
    const y = Math.round(posArray[i * 3 + 1] / EPSILON) * EPSILON;
    const z = Math.round(posArray[i * 3 + 2] / EPSILON) * EPSILON;
    const key = `${x.toFixed(4)},${y.toFixed(4)},${z.toFixed(4)}`;
    if (!posMap.has(key)) posMap.set(key, []);
    posMap.get(key)!.push(i);
  }

  // Build vertex→face map using shared positions
  const vertexToFaces = new Map<number, number[]>();
  for (let fi = 0; fi < faceCount; fi++) {
    const baseIdx = fi * 3;
    for (let vi = 0; vi < 3; vi++) {
      const vertIdx = baseIdx + vi;
      if (!vertexToFaces.has(vertIdx)) vertexToFaces.set(vertIdx, []);
      vertexToFaces.get(vertIdx)!.push(fi);

      // Also link to faces sharing the same position
      const x = Math.round(posArray[vertIdx * 3] / EPSILON) * EPSILON;
      const y = Math.round(posArray[vertIdx * 3 + 1] / EPSILON) * EPSILON;
      const z = Math.round(posArray[vertIdx * 3 + 2] / EPSILON) * EPSILON;
      const key = `${x.toFixed(4)},${y.toFixed(4)},${z.toFixed(4)}`;
      const shared = posMap.get(key);
      if (shared) {
        for (const sharedVert of shared) {
          const sharedFace = Math.floor(sharedVert / 3);
          if (sharedFace !== fi) {
            vertexToFaces.get(vertIdx)!.push(sharedFace);
          }
        }
      }
    }
  }

  const adj: AdjacencyIndex = { vertexToFaces, faceCount, geometryUuid: uuid };
  adjacencyCache.set(uuid, adj);
  return adj;
}

/**
 * Flood-fill from a hit face to find all connected faces (the component).
 *
 * @param geometry - The Three.js BufferGeometry
 * @param hitFaceIndex - The face index from raycaster intersection
 * @param maxFaces - Safety cap to prevent runaway flood-fill on degenerate meshes
 * @returns Set of face indices belonging to the same component, or null if failed
 */
export function floodFillComponent(
  geometry: BufferGeometry,
  hitFaceIndex: number,
  maxFaces = 100_000,
): Set<number> | null {
  const adj = geometry.index
    ? getAdjacency(geometry)
    : getAdjacencyNonIndexed(geometry);

  if (!adj) return null;

  const indexArray = geometry.index?.array;
  const visited = new Set<number>();
  const queue: number[] = [hitFaceIndex];
  visited.add(hitFaceIndex);

  while (queue.length > 0 && visited.size < maxFaces) {
    const faceIdx = queue.pop()!;

    // Get the 3 vertex indices of this face
    let v0: number, v1: number, v2: number;
    if (indexArray) {
      v0 = indexArray[faceIdx * 3];
      v1 = indexArray[faceIdx * 3 + 1];
      v2 = indexArray[faceIdx * 3 + 2];
    } else {
      v0 = faceIdx * 3;
      v1 = faceIdx * 3 + 1;
      v2 = faceIdx * 3 + 2;
    }

    // Find all neighbor faces (faces sharing any vertex with this face)
    for (const vertIdx of [v0, v1, v2]) {
      const neighborFaces = adj.vertexToFaces.get(vertIdx);
      if (!neighborFaces) continue;
      for (const nf of neighborFaces) {
        if (!visited.has(nf)) {
          visited.add(nf);
          queue.push(nf);
        }
      }
    }
  }

  return visited;
}

/**
 * Try to read _BATCH_ID attribute from the geometry (if the converter embedded it).
 * Falls back to flood-fill if the attribute doesn't exist.
 *
 * @returns component face set, or null
 */
export function getComponentByBatchId(
  geometry: BufferGeometry,
  hitFaceIndex: number,
): Set<number> | null {
  const batchAttr =
    geometry.attributes._batchid ??          // Giro3D / 3DTiles-renderer (actual name)
    geometry.attributes['_batchid'] ??
    geometry.attributes._BATCHID ??
    geometry.attributes['_BATCHID'] ??
    geometry.attributes._BATCH_ID ??
    geometry.attributes['_BATCH_ID'] ??
    geometry.attributes.batchId ??
    geometry.attributes['batchId'];
  if (!batchAttr) return null;

  const batchArray = batchAttr.array;
  const indexArray = geometry.index?.array;

  // Get the batch ID of the hit face (read from first vertex of face)
  let hitVertIdx: number;
  if (indexArray) {
    hitVertIdx = indexArray[hitFaceIndex * 3];
  } else {
    hitVertIdx = hitFaceIndex * 3;
  }

  const targetBatchId = batchArray[hitVertIdx];

  // Collect all faces with the same batch ID
  const faceCount = indexArray
    ? Math.floor(indexArray.length / 3)
    : Math.floor((geometry.attributes.position?.count ?? 0) / 3);

  const result = new Set<number>();
  for (let fi = 0; fi < faceCount; fi++) {
    let vi: number;
    if (indexArray) {
      vi = indexArray[fi * 3];
    } else {
      vi = fi * 3;
    }
    if (batchArray[vi] === targetBatchId) {
      result.add(fi);
    }
  }

  return result;
}

/**
 * Get the face indices of a component — tries batch ID first, then flood-fill.
 */
export function isolateComponent(
  geometry: BufferGeometry,
  hitFaceIndex: number,
): Set<number> | null {
  // Fast path: use pre-computed batch IDs if available
  const byBatch = getComponentByBatchId(geometry, hitFaceIndex);
  if (byBatch && byBatch.size > 0) return byBatch;

  // Slow path: geometric flood-fill
  return floodFillComponent(geometry, hitFaceIndex);
}

/**
 * Resolve a stable feature ID from glTF metadata copied by Three's GLTFLoader.
 *
 * Phase 5 tiles mirror the stable ID in `extras.geosoft.featureId` because the
 * full EXT_structural_metadata table is not decoded by Three.js yet. The
 * attribute fallback handles older batch-ID style outputs.
 */
export function readFeatureIdFromObject(object: unknown, hitFaceIndex?: number | null): string | null {
  let current = asRecord(object);
  while (current) {
    const fromUserData = readFeatureIdFromUserData(asRecord(current.userData));
    if (fromUserData) return fromUserData;

    const geometry = asRecord(current.geometry);
    const fromGeometry = readFeatureIdFromUserData(asRecord(geometry?.userData));
    if (fromGeometry) return fromGeometry;

    const fromAttribute = geometry && typeof hitFaceIndex === 'number'
      ? readFeatureIdFromAttribute(geometry, hitFaceIndex)
      : null;
    if (fromAttribute) return fromAttribute;

    current = asRecord(current.parent);
  }
  return null;
}

export function objectMatchesFeatureId(object: unknown, featureId: string): boolean {
  const current = asRecord(object);
  if (!current) return false;
  if (readFeatureIdFromObject(current) === featureId) return true;
  const children = Array.isArray(current.children) ? current.children : [];
  return children.some((child) => objectMatchesFeatureId(child, featureId));
}

/**
 * Create a highlight geometry from a set of face indices.
 * Returns a new BufferGeometry containing only the selected faces.
 */
export function createComponentGeometry(
  sourceGeometry: BufferGeometry,
  faceIndices: Set<number>,
): BufferGeometry | null {
  // Dynamically import Three.js
  const THREE = (window as any).__THREE__;
  if (!THREE) return null;

  const indexArray = sourceGeometry.index?.array;
  const posAttr = sourceGeometry.attributes.position;
  if (!posAttr) return null;

  const posArray = posAttr.array as Float32Array;

  // Extract face positions
  const positions: number[] = [];

  for (const fi of faceIndices) {
    let v0: number, v1: number, v2: number;
    if (indexArray) {
      v0 = indexArray[fi * 3];
      v1 = indexArray[fi * 3 + 1];
      v2 = indexArray[fi * 3 + 2];
    } else {
      v0 = fi * 3;
      v1 = fi * 3 + 1;
      v2 = fi * 3 + 2;
    }

    positions.push(
      posArray[v0 * 3], posArray[v0 * 3 + 1], posArray[v0 * 3 + 2],
      posArray[v1 * 3], posArray[v1 * 3 + 1], posArray[v1 * 3 + 2],
      posArray[v2 * 3], posArray[v2 * 3 + 1], posArray[v2 * 3 + 2],
    );
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}

function readFeatureIdFromUserData(userData: UnknownRecord | null): string | null {
  if (!userData) return null;
  const direct = readString(userData.featureId);
  if (direct) return direct;
  const geosoft = asRecord(userData.geosoft);
  const fromGeosoft = readString(geosoft?.featureId);
  if (fromGeosoft) return fromGeosoft;

  const extensions = asRecord(userData.gltfExtensions);
  const extensionGeosoft = asRecord(extensions?.geosoft);
  const fromExtension = readString(extensionGeosoft?.featureId);
  if (fromExtension) return fromExtension;
  return null;
}

function readFeatureIdFromAttribute(geometry: UnknownRecord, hitFaceIndex: number): string | null {
  const attributes = asRecord(geometry.attributes);
  const attr = asRecord(
    attributes?._FEATURE_ID_0 ??
    attributes?.['_FEATURE_ID_0'] ??
    attributes?._batchid ??           // Giro3D / 3DTiles-renderer (actual name)
    attributes?.['_batchid'] ??
    attributes?._BATCHID ??
    attributes?.['_BATCHID'] ??
    attributes?._BATCH_ID ??
    attributes?.['_BATCH_ID'] ??
    attributes?.batchId ??
    attributes?.['batchId'],
  );
  const array = attr?.array as ArrayLike<number> | undefined;
  if (!array) return null;
  const index = asRecord(geometry.index);
  const indexArray = index?.array as ArrayLike<number> | undefined;
  const vertexIndex = indexArray ? indexArray[hitFaceIndex * 3] : hitFaceIndex * 3;
  const value = array[vertexIndex];
  if (!Number.isFinite(value)) return null;

  const table = readFeatureTable(asRecord(geometry.userData));
  const tableFeatureId = table?.[value]?.featureId;
  if (typeof tableFeatureId === 'string' && tableFeatureId.length > 0) return tableFeatureId;

  // Return the batch ID as a stable feature identifier (e.g. "feature:42")
  const hasBatch =
    attributes?._batchid ?? attributes?.['_batchid'] ??
    attributes?._BATCHID ?? attributes?.['_BATCHID'] ??
    attributes?._BATCH_ID ?? attributes?.['_BATCH_ID'] ??
    attributes?.batchId ?? attributes?.['batchId'];
  return hasBatch ? `feature:${Math.trunc(value)}` : null;
}

function readFeatureTable(userData: UnknownRecord | null): Array<{ featureId?: string }> | null {
  const geosoft = asRecord(userData?.geosoftFeatureMetadata ?? userData?.geosoft);
  const features = geosoft?.features;
  return Array.isArray(features) ? features as Array<{ featureId?: string }> : null;
}

function asRecord(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null ? value as UnknownRecord : null;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Clear cached adjacency data (call on scene unload).
 */
export function clearAdjacencyCache(): void {
  adjacencyCache.clear();
}
